import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MarkdownEditor } from '@/components/MarkdownEditor';
import { useEmailer } from '@/state/useEmailer';
import { cn } from 'helpers/cn';
import { Meter, Panel } from '../../Dashboard/ConsolePanel';
import { EmailPreview } from './EmailPreview';

const FIELD_INPUT = 'border-line bg-field text-foreground h-10 rounded-lg text-body';
const SECONDARY_BUTTON = 'border-line text-foreground hover:bg-hover h-9 rounded-lg bg-transparent';

/** Every recipient row is the same shape: a box, then a label that dims when the box is locked. */
type RecipientRowProps = {
  id: string;
  name: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  locked?: boolean;
  onChange: (checked: boolean) => void;
};

const RecipientRow = ({ id, name, label, checked, disabled, locked, onChange }: RecipientRowProps) => (
  <div className="flex items-center gap-2">
    <Checkbox id={id} name={name} checked={checked} disabled={disabled} onCheckedChange={(next) => onChange(!!next)} />
    <label
      htmlFor={id}
      className={cn('text-foreground text-meta', locked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}
    >
      {label}
    </label>
  </div>
);

export function Emailer() {
  const { formManager, send, sendTest, getRecipientCount, reset, isLoading, progress } = useEmailer();
  const { state: formState, formRef, update } = formManager;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [userInput, setUserInput] = useState('');
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [fetchingRecipients, setFetchingRecipients] = useState(false);
  const [checklist, setChecklist] = useState({
    subject: false,
    greeting: false,
    body: false,
    preview: false,
    recipients: false,
  });

  const generateConfirmationCode = () => {
    const chars = 'ABCDEFGHIJKLMNPQRSTUVWXYZ';
    let code = '';
    for (let i = 0; i < 5; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleSendClick = async () => {
    try {
      setFetchingRecipients(true);
      const count = await getRecipientCount();
      setRecipientCount(count);

      const code = generateConfirmationCode();
      setConfirmationCode(code);
      setUserInput('');
      setShowConfirmModal(true);
    } catch (error) {
      console.error('Failed to get recipient count:', error);
    } finally {
      setFetchingRecipients(false);
    }
  };

  const handleConfirmSend = async () => {
    if (userInput.trim().toUpperCase() === confirmationCode) {
      setShowConfirmModal(false);
      await send();
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setConfirmationCode('');
    setUserInput('');
    setChecklist({ subject: false, greeting: false, body: false, preview: false, recipients: false });
  };

  const allChecklistItemsChecked = Object.values(checklist).every((checked) => checked);
  const isConfirmButtonEnabled = allChecklistItemsChecked && userInput.trim().toUpperCase() === confirmationCode;

  const hasRecipients = formState.sendTo?.everyone || formState.sendTo?.owners || formState.sendTo?.admins;
  const hasSubject = formState.subject && formState.subject.trim().length > 0;
  const hasContent = formState.content && formState.content.trim().length > 0;
  const canSend = hasRecipients && hasSubject && hasContent;
  const canSendTest = hasSubject && hasContent;

  const isSending = progress.status === 'sending';
  const progressPercent = progress.total > 0 ? Math.round((progress.sent / progress.total) * 100) : 0;

  return (
    <div className="flex flex-col gap-[14px]">
      {isSending && (
        <Panel title="Sending" description="Leave this page open until the run finishes.">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3">
              <span className="micro-label">sending emails</span>
              <span className="num text-foreground">
                {progress.sent} / {progress.total}
              </span>
            </div>
            <Meter percent={progressPercent} className="bg-accent" />
          </div>
        </Panel>
      )}

      {progress.status === 'complete' && (
        <div className="border-ok-line rounded-panel border px-3.5 py-2.5">
          <p className="text-ok m-0 text-meta">
            Successfully sent {progress.sent} emails.
            {progress.failed ? ` ${progress.failed} failed.` : ''}
          </p>
        </div>
      )}

      <Panel title="Broadcast" description="Send an email from Pertento to our customers">
        <form ref={formRef}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="subject" className="micro-label block">
                Subject
              </label>
              <Input
                id="subject"
                name="subject"
                placeholder="Enter email subject"
                defaultValue={formState.subject}
                disabled={isSending}
                className={FIELD_INPUT}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="greeting" className="micro-label block">
                Greeting (optional)
              </label>
              <Input
                id="greeting"
                name="greeting"
                placeholder="e.g., Hi {{firstName}}, or Hello {{fullName}},"
                defaultValue={(formState as any).greeting || ''}
                disabled={isSending}
                className={FIELD_INPUT}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="content" className="micro-label block">
                Content
              </label>
              <div className="min-h-64">
                <MarkdownEditor
                  content={formState.content || ''}
                  onChange={(content: string) =>
                    update({
                      ...formState,
                      content,
                    })
                  }
                  placeholder="Enter your email content in markdown..."
                  preview={
                    <EmailPreview
                      subject={formState.subject || ''}
                      greeting={formState.greeting || ''}
                      content={formState.content || ''}
                    />
                  }
                />
              </div>
            </div>

            <div className="border-line rounded-card flex flex-wrap items-end justify-between gap-4 border p-3">
              <div className="flex flex-col gap-2.5">
                <span className="micro-label block">Send to</span>
                <div className="flex flex-col gap-2">
                  <RecipientRow
                    id="everyone"
                    name="sendTo_everyone"
                    label="Everyone"
                    checked={formState.sendTo?.everyone || false}
                    disabled={isSending}
                    onChange={(checked) => update({ ...formState, sendTo: { ...formState.sendTo, everyone: checked } })}
                  />
                  <RecipientRow
                    id="owners"
                    name="sendTo_owners"
                    label="Owners"
                    checked={formState.sendTo?.owners || false}
                    disabled={formState.sendTo?.everyone || isSending}
                    locked={formState.sendTo?.everyone}
                    onChange={(checked) => update({ ...formState, sendTo: { ...formState.sendTo, owners: checked } })}
                  />
                  <RecipientRow
                    id="admins"
                    name="sendTo_admins"
                    label="Admins"
                    checked={formState.sendTo?.admins || false}
                    disabled={formState.sendTo?.everyone || isSending}
                    locked={formState.sendTo?.everyone}
                    onChange={(checked) => update({ ...formState, sendTo: { ...formState.sendTo, admins: checked } })}
                  />
                  <RecipientRow
                    id="excludeConverdiant"
                    name="sendTo_excludeConverdiant"
                    label="Exclude Converdiant"
                    checked={formState.sendTo?.excludeConverdiant || false}
                    disabled={isSending}
                    onChange={(checked) =>
                      update({ ...formState, sendTo: { ...formState.sendTo, excludeConverdiant: checked } })
                    }
                  />
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="outline" onClick={reset} disabled={isSending} className={SECONDARY_BUTTON}>
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  onClick={sendTest}
                  disabled={!canSendTest || isLoading || isSending}
                  className={SECONDARY_BUTTON}
                >
                  Send Test
                </Button>
                <Button
                  onClick={handleSendClick}
                  disabled={!canSend || isLoading || fetchingRecipients || isSending}
                  className="h-9 rounded-lg"
                >
                  {fetchingRecipients ? 'Loading...' : 'Send'}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Panel>

      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Email Send</DialogTitle>
            <DialogDescription className="text-foreground">
              This action will send an email to <strong>{recipientCount}</strong> recipient
              {recipientCount !== 1 ? 's' : ''}. To confirm, please enter the following code:
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="border-line rounded-card border p-3">
              <p className="micro-label m-0 mb-2.5">Have you checked</p>
              <div className="space-y-2">
                <RecipientRow
                  id="check-subject"
                  name="check-subject"
                  label="the Subject?"
                  checked={checklist.subject}
                  onChange={(checked) => setChecklist({ ...checklist, subject: checked })}
                />
                <RecipientRow
                  id="check-greeting"
                  name="check-greeting"
                  label="the Greeting?"
                  checked={checklist.greeting}
                  onChange={(checked) => setChecklist({ ...checklist, greeting: checked })}
                />
                <RecipientRow
                  id="check-body"
                  name="check-body"
                  label="the Body of the email?"
                  checked={checklist.body}
                  onChange={(checked) => setChecklist({ ...checklist, body: checked })}
                />
                <RecipientRow
                  id="check-preview"
                  name="check-preview"
                  label="the Preview?"
                  checked={checklist.preview}
                  onChange={(checked) => setChecklist({ ...checklist, preview: checked })}
                />
                <RecipientRow
                  id="check-recipients"
                  name="check-recipients"
                  label={`The Recipients (${recipientCount})?`}
                  checked={checklist.recipients}
                  onChange={(checked) => setChecklist({ ...checklist, recipients: checked })}
                />
              </div>
            </div>

            <div className="bg-field rounded-card select-none p-3.5">
              <p className="text-foreground pointer-events-none m-0 text-center font-mono text-lg tracking-[0.3em]">
                {confirmationCode}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="confirm-input" className="micro-label block">
                Enter the code above
              </label>
              <Input
                id="confirm-input"
                type="text"
                placeholder="Enter the 5-character code"
                value={userInput}
                onChange={(ev) => setUserInput(ev.target.value.toUpperCase())}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter' && isConfirmButtonEnabled) {
                    handleConfirmSend();
                  }
                }}
                className={FIELD_INPUT}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={handleCancelConfirm} className={SECONDARY_BUTTON}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmSend}
                disabled={!isConfirmButtonEnabled || isLoading}
                className="h-9 rounded-lg"
              >
                Confirm Send
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
